// ============================================================================
// TypeScript types matching the TCS Supabase schema (v3.4 seed → production).
// Schema reference: project-readmes/tcs-erp-database-README.md (46 tabs)
// Module reference: app-readmes/account-management-README.md
//
// Naming convention: snake_case in the database; we mirror that here so we
// can pass typed objects directly into supabase-js calls without translation.
// ============================================================================

// ----- Enums (string literal unions match Postgres CHECK constraints) -------

// WS1/WS5 2026-07-31: canonical five, underscore spelling, matches the live
// facilities_facility_type_canonical CHECK. 'ASC' and 'Endoscopy Center' no
// longer exist in data (renamed to Surgery_Center); 'Office' was retired.
export type FacilityType = 'Surgery_Center' | 'Hospital' | 'Dialysis' | 'MOB' | 'Other';

export type RagStatus = 'Green' | 'Amber' | 'Red';

// 5-category mapping for the AM-owned RAG (2026-05-21). CHECK constraint
// on facilities.rag_category enforces this set. See app-readmes/
// account-management-README.md → "RAG editing" for the meaning of each.
export type RagCategory =
  | 'Inspection'
  | 'Client_Feedback'
  | 'Sub_Transition'
  | 'Operational'
  | 'Other';

export type FeedbackSentiment = 'Positive' | 'Neutral' | 'Negative' | 'Critical';

export type FeedbackCategory =
  | 'Cleaning_Quality'
  | 'Equipment'
  | 'Supplies'
  | 'Staff_Behavior'
  | 'Safety'
  | 'Security'
  | 'Communication'
  | 'Facility_Damage'
  | 'Other';

export type FeedbackStatus =
  | 'Open'
  | 'In_Progress'
  | 'Resolved'
  | 'Recurring'
  | 'Closed';

export type FeedbackSource =
  | 'Client_Direct'
  | 'AM_Observation'
  | 'Night_Manager'
  | 'Cleaning_Crew'
  | 'Inspection'
  | 'Aaron_App'
  | 'Other';

export type TaskPriority = 'High' | 'Medium' | 'Low';
export type TaskStatus = 'Open' | 'In_Progress' | 'Done' | 'Deferred';
export type TaskSource =
  | 'Manual'
  | 'Feedback_Auto'
  | 'Inspection_Auto'
  | 'Supply_Auto'
  // System tasks minted by the sub-onboarding soft gate (2026-07-06) and the
  // AM copilot — both exist in live facility_tasks.source.
  | 'Sub_Auto'
  | 'AM_Copilot';

// Matches the `role` text values in public.user_profiles. Stored as text
// (not a Postgres enum) so RLS helpers can compare with simple `= 'superuser'`.
//
// Source of truth: user_profiles_role_check (2026-05-10 expansion):
//   pending, superuser, executive, national_am, account_manager,
//   ops, hr, finance, sales
//
// 'pending' is the default for auto-provisioned users — they have a row
// but an admin hasn't classified them yet. has_full_am_authority returns
// true for superuser/executive/national_am.
export type UserRole =
  | 'pending'
  | 'superuser'
  | 'executive'
  | 'national_am'
  | 'account_manager'
  | 'ops'
  | 'hr'
  | 'finance'
  | 'sales'
  // 'intern' is accepted by user_profiles_role_check and by the extra_roles
  // CHECK, and was missing here — so a row the database considers valid was
  // untypeable in the client. Nobody holds it today. Added 2026-08-20 when a
  // role-gate test tried to prove 'intern' is refused and could not name it.
  | 'intern';

// Matches user_profiles.status. Source: user_profiles_status_check.
//   pending  — auto-provisioned, awaiting admin approval
//   active   — normal access; RLS gives them whatever their role allows
//   disabled — JWT bridge 403s before minting a token
export type UserStatus = 'pending' | 'active' | 'disabled';

// ----- Core entities --------------------------------------------------------

// Mirrors public.employees actual columns. Naming oddities (employee_name_first
// vs first_name, email_work vs email) are intentional — they match the DB.
export interface Employee {
  employee_id: string; // EID-XXXX
  employee_name_first: string | null;
  employee_name_last: string | null;
  email_work: string | null;
  email_personal: string | null;
  position_id: string | null;
  role_type: string | null;
  primary_facility_id: string | null;
  employment_status: string | null;
}

// Mirrors public.user_profiles. `id` is the auth.users UUID and equals the
// JWT `sub` claim, so RLS helpers like is_superuser() resolve via auth.uid().
//
// 2026-05-10 expansion added: firebase_uid, status, email, last_login_at,
// notes, updated_by. status governs whether the JWT bridge will mint a
// token (disabled -> 403) and which screen the client routes to (pending
// -> /pending-approval, active -> normal nav).
export interface UserProfile {
  id: string; // uuid (== auth.users.id == JWT sub)
  employee_id: string | null;
  role: UserRole;
  // Additive capability "hats" granted on top of the primary role (e.g. a
  // finance-write hat for an HR user). Source: user_profiles.extra_roles.
  // Gate finance-write UI on hasFinanceCapability() (./role-gates), not on role equality.
  extra_roles: UserRole[];
  status: UserStatus;
  display_name: string | null;
  email: string | null;
  firebase_uid: string | null;
  last_login_at: string | null;
  notes: string | null;
  updated_by: string | null; // uuid of admin who last touched the row
  created_at: string | null;
  updated_at: string | null;
}

// The role gates — hasFinanceCapability, canViewManagementFee,
// canViewRestrictedHr and every other "who may see or do this" helper — live
// in ./role-gates.ts, the one place a role word is compared.

// The user's primary role plus any granted capability "hats". Use when a gate
// should consider both (e.g. a future capability-aware nav filter). Keep the
// role list/logic in hasFinanceCapability() (./role-gates) as the single source
// of truth for the finance capability itself.
export function effectiveRoles(
  profile: Pick<UserProfile, 'role' | 'extra_roles'> | null | undefined,
): UserRole[] {
  if (!profile) return [];
  return [profile.role, ...(profile.extra_roles ?? [])];
}

// Returned by the firebase-jwt-bridge Edge Function. Mirrors the JSON body.
// `status` was added 2026-05-11 alongside auto-provisioning — the bridge
// already 403s disabled users, so the client only ever sees 'pending' or
// 'active' here, but typing it explicitly keeps the gate code honest.
//
// employee_id is now nullable: auto-provisioned (pending) users don't yet
// have an employee link — provision_user fills it in on approval.
export interface BridgedUser {
  id: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  employee_id: string | null;
  display_name: string | null;
  firebase_uid: string;
}

// 2026-08-03: this interface used to declare optional `account_sentiment` /
// `sentiment_updated_date` fields. The `accounts` table has never had those
// columns — only `facilities` did — so the declaration was a phantom that
// taught every reader a false schema. Removed. Sentiment is DERIVED from
// facility_feedback; see tcs-app-prompt-retire-account-sentiment-2026-08-03.md.
export interface Account {
  account_id: string;
  account_name: string;
}

// Lifecycle status from public.facilities.status. The column is plain `status`
// (not `facility_status`) and it is free text, not an enum.
//
// 2026-08-20: 'Merged' added. No row carries it TODAY (202 Active, 28 Closed,
// 5 Onboarding, 2 Pending), but the database treats it as a real live-record
// value everywhere that matters — zz_enforce_facility_closure, every finance
// and route view, and all three lifecycle RPCs guard on
// `status NOT IN ('Closed','Merged')`. Leaving it out of the union told
// TypeScript that a check for it was dead code, which is how a UI ends up
// offering to pause a merged record.
export type FacilityLifecycleStatus =
  | 'Active'
  | 'Pending'
  | 'Onboarding'
  | 'Closed'
  | 'Merged'
  | 'FLAG';

// ----- The SECOND lifecycle axis (TCSD-0003) --------------------------------
//
// `status` above answers "is this record live?" — Active / Onboarding / Pending
// / Closed / Merged. `service_state` answers a DIFFERENT question: "are we
// cleaning it this week?" A paused facility is Active/Suspended: the record is
// live, the relationship is intact, the rate is preserved, and nobody is on
// site. A terminated one is Closed/Ended.
//
// THESE TWO ARE NOT INTERCHANGEABLE AND MUST NEVER BE COLLAPSED. Reading
// `status` alone is what let a paused client keep appearing as billable
// revenue; reading `service_state` alone loses the closed-vs-merged
// distinction. Anything that asks "are we serving them" needs both.
//
// Written only by rpc_pause_facility / rpc_resume_facility / rpc_end_facility
// (and the close trigger). Never edit these fields inline — the RPCs also move
// the sub's assignment, the finance notice and the snapshot, and a hand-edit
// leaves a facility whose crew is still assigned to it.
export type FacilityServiceState = 'Active' | 'Suspended' | 'Ended';

// Why the relationship stands where it does, for the sales team. Not the same
// question as the reason code: a `client_budget_hold` pause is At_Risk, an
// `under_construction` pause is Retained, and both are pauses.
export type FacilityRelationshipIntent = 'Retained' | 'At_Risk' | 'Lost';

// Whose decision it was. Drives churn attribution reporting.
export type FacilityAttribution =
  | 'Client_Choice'
  | 'TCS_Performance'
  | 'External'
  | 'Administrative';

export interface Facility {
  facility_id: string; // FAC-XXXX
  facility_name: string;
  account_id: string | null;
  facility_type: FacilityType | null;
  facility_address: string | null;
  // State code (CO/MO/KS/TX/...). Two-letter USPS abbreviation.
  state: string | null;
  // Lifecycle (Active / Pending / Closed / FLAG). Real DB column is `status`.
  status: FacilityLifecycleStatus | null;
  // ----- Service state: the second lifecycle axis --------------------------
  // These columns existed in the database from Phase 1 of
  // facility-pause-terminate but appeared NOWHERE in src/ — the app was
  // completely blind to the pause model, so a paused facility rendered
  // identically to a serviced one. Read them; do not write them (see the
  // FacilityServiceState comment above).
  //
  // `service_state` is NULL on rows never touched by the pause RPCs; treat
  // NULL as 'Active'. That is why every reader here coalesces rather than
  // testing equality with 'Active'.
  service_state?: FacilityServiceState | null;
  service_state_changed_at?: string | null; // ISO timestamptz
  // Set while Suspended, cleared on resume. FK-ish → facility_lifecycle_reasons
  // (reason_code) where kind='pause'.
  pause_reason?: string | null;
  // Set when service ends. Same lookup table, kind='end'.
  end_reason?: string | null;
  // The date the client said they would be back. NULL means indefinite, which
  // is exactly the case the pause RPC forces a note for.
  expected_resume_date?: string | null; // date
  relationship_intent?: FacilityRelationshipIntent | null;
  attribution?: FacilityAttribution | null;
  // Append-only human trail written by the pause/resume RPCs. One stamped line
  // per transition — display it, never parse it.
  lifecycle_note?: string | null;
  market_region: string | null;
  account_manager_eid: string | null; // FK → Employees
  account_manager_name: string | null;
  /**
   * The BD who won this building, carried from the onboarding card's bdd_eid
   * (TCSD-0225). It lives here rather than on the card because the card closes
   * and this fact outlives it by years. NULL on a building that predates the
   * card system — never inferred from anything.
   */
  sold_by_eid?: string | null;
  /** Resolved at read time from sold_by_eid; never a stored column. */
  sold_by_name?: string | null;
  /**
   * The onboarding card this building was created from, when it was. NULL on
   * the buildings that predate the card system. Declared here 2026-09-16 so the
   * Info tab can offer a route back to that card — the column has been on
   * `facilities` and arriving through `select('*')` all along; only the type
   * did not say so.
   */
  source_sop_id?: string | null;
  /** Supply cost budget as a percentage of revenue. Typed in at setup; not
   *  measured from actual purchases (item 745). */
  supply_cost_pct?: number | null;
  // Start dates (NEW ACCOUNTS lifecycle, PR-4a). planned_start_date drives the
  // countdown; contract_start is the REAL go-live date that anchors billing,
  // the annual kicker, and proration (set/corrected via the start-date RPCs).
  planned_start_date?: string | null;
  contract_start?: string | null;
  // Pricing — client billing rate (what we charge). Distinct semantic from
  // sub cost, which is now derived live from service_assignments per the
  // 2026-05-19 facility-detail-consolidation cowork (single source of truth).
  monthly_rate: number | null;
  // AFB-0041 (2026-07-02) — management fee + usables surfacing. The fee is
  // facilities.management_fee_pct, a FRACTION (0.0300 = 3%); NULL or 0 both
  // mean "no fee". Do not confuse with last/next_kicker_date escalation
  // fields, which are untouched by the UI.
  management_fee_pct?: number | null;
  tcs_provides_usables?: boolean | null;
  usables_detail?: Record<string, unknown> | null;
  // Facility Relationships Model (2026-07-17) — LOCATION axis only. building_id
  // groups facilities that share a roof; is_pinned calls a member out separately
  // even in a consolidated card; is_common_area marks a building-level line.
  // None of these are billing fields — billing stays on service_assignments.
  building_id?: string | null;
  is_pinned?: boolean | null;
  is_common_area?: boolean | null;
  // RAG fields (column is `account_status_rag` in the DB).
  account_status_rag: RagStatus | null;
  // 2026-07-27: the legacy rag_changed_date / rag_change_reason pair was
  // dropped from facilities. RAG metadata now has exactly one home — the four
  // attribution columns below. Do not add a parallel "when/why" field.
  // AM-owned attribution columns (2026-05-21). Populated whenever the AM
  // (or the COO / superuser) changes account_status_rag from the UI; the
  // KPI-doc backfill populated rag_set_at + rag_reason + rag_category but
  // left rag_set_by NULL (no per-row employee in the source data).
  rag_set_by: string | null; // EID-XXXX → employees.employee_id
  rag_set_at: string | null; // ISO timestamp with tz
  rag_reason: string | null;
  rag_category: RagCategory | null;
  // Financial RAG dimension removed 2026-07-06; Sub RAG dimension
  // (sub_status_rag) retired 2026-07-31 — see
  // tcs-app-prompt-retire-sub-rag-2026-07-31.md. account_status_rag above is
  // the ONLY RAG dimension. Do not add a third.
  // 2026-08-03: the stored `account_sentiment` / `sentiment_updated_date`
  // columns were RETIRED and dropped from facilities — see
  // tcs-app-prompt-retire-account-sentiment-2026-08-03.md. They held 15 stale
  // machine-written values in a Frustrated/Happy vocabulary the rest of the
  // system never accepted, and zero humans ever wrote one. Facility sentiment
  // is DERIVED from facility_feedback.sentiment (the CHECK-constrained source
  // of truth) and surfaces as `latest_sentiment`. Do not re-add a stored
  // sentiment field here.
  // Geo (for the map view).
  latitude: number | null;
  longitude: number | null;
  // Geocode PROVENANCE (not accuracy — see the 2026-08-05 retraction in
  // tcs-erp-database-README.md). These three say WHERE the coordinate above
  // came from, and CW-C Phase 4 routes on them: only geocode_precision =
  // 'ROOFTOP' earns coordinate-first navigation. NULL precision with
  // geocode_source = 'Legacy_Unknown' is the honest state of 173 facilities and
  // is deliberately NOT being backfilled (Ben's Option C, 2026-08-05).
  geocode_precision?: string | null;
  geocode_source?: string | null;
  geocoded_at?: string | null; // ISO timestamptz
  // FIELD verification (Option C, 2026-08-05) — a human who physically went
  // there. Deliberately SEPARATE from geocode_precision above: that records what
  // Google claimed, this records what someone actually found. It is the only way
  // a Legacy_Unknown coordinate can become trusted for navigation, because
  // re-geocoding would only confirm Google against itself.
  coord_verified_result?: 'confirmed' | 'wrong' | null;
  coord_verified_by?: string | null;   // EID-XXXX → employees.employee_id
  coord_verified_at?: string | null;   // ISO timestamptz
  coord_verified_note?: string | null;
  // Contract on file (Drive URL). Added 2026-05-11. NULL = no link recorded.
  client_contract_url: string | null;
  // Closure metadata (added 2026-05-12). Only populated when status='Closed'
  // or 'Cancelled'; the active_facilities view excludes those rows so
  // operational queries never see these fields, but facility-detail.tsx
  // and the closure banner read them off the base facilities table.
  closed_date: string | null;
  close_reason: string | null;
  // Billing cluster (added 2026-05-26). NULL on solo facilities. When set,
  // this facility is part of a multi-facility billing arrangement where
  // one payer covers multiple service locations. See
  // tcs-erp-database-README.md § Billing Cluster Pattern.
  billing_account_id: string | null;
  is_billing_primary: boolean; // NOT NULL; defaults true. False on bundled
                               // service locations under another facility's
                               // contract.
  // AM validation layer (Cowork 04, D5, 2026-06-28). Any active TCS employee
  // (BDD/sales) can now enter/edit account data; the assigned AM / national AM
  // / COO confirms it by stamping these two columns. NULL last_confirmed_at =
  // "Awaiting AM validation"; set = "Validated ✓ <date>".
  last_confirmed_by_eid?: string | null; // EID-XXXX → employees.employee_id
  last_confirmed_at?: string | null; // ISO timestamptz
}

export interface FacilityFeedback {
  feedback_id: string; // FBK-XXXX
  facility_id: string;
  account_id: string | null;
  feedback_date: string; // ISO date
  reported_by_contact_id: string | null;
  reported_by_employee_id: string | null;
  sentiment: FeedbackSentiment;
  category: FeedbackCategory;
  description: string;
  resolution: string | null;
  resolution_date: string | null;
  resolved_by: string | null;
  issue_count: number;
  resolved_count: number;
  recurrence_count: number;
  status: FeedbackStatus;
  source: FeedbackSource;
  // AM Phase 2: optional FK to incentive_payouts when this feedback row
  // (compliment or miss) qualified a bonus payout. Null for the vast majority
  // of rows. Set by accrual logic, surfaced in the activity feed.
  payout_id?: string | null;
  // Incentive discretion (D2, incentive program 2026-06-05). DB defaults both
  // to false. The feedback form pre-checks counts_against_nmi when sentiment
  // is Negative/Critical and counts_toward_compliment when Positive, but both
  // stay AM-editable. A complaint is NOT an NMI disqualification unless
  // counts_against_nmi is true (the "Rose turned it around" case).
  counts_against_nmi?: boolean;
  counts_toward_compliment?: boolean;
}

// Matches facility_tasks_category_check (migration facility_tasks_category_2026_05_24).
// 'Operations' is the default for AM-facing tasks; Finance/HR/Sub_Management/
// Sales/Other route to specific role inboxes (Finance + HR widgets on the
// dashboard surface a count of Open tasks per category for their assignee).
export type TaskCategory =
  | 'Operations'
  | 'Finance'
  | 'HR'
  | 'Sub_Management'
  | 'Sales'
  | 'Other';

// Lightweight task comment thread element, stored in facility_tasks.comments
// (jsonb array). Lets an assignee give feedback to the creator without — or
// before — closing the task (AFB-0012). The closing resolution note is also
// appended here as the final comment.
export interface TaskComment {
  comment_id: string;
  author_eid: string;
  body: string;
  created_at: string; // ISO timestamp
}

export interface FacilityTask {
  task_id: string; // FTK-XXXX
  facility_id: string;
  account_id: string | null;
  description: string;
  priority: TaskPriority;
  status: TaskStatus;
  category: TaskCategory | null;
  created_by: string;
  assigned_to: string | null;
  due_date: string | null;
  source: TaskSource;
  related_feedback_id: string | null;
  completion_notes: string | null;
  completed_date: string | null;
  completed_by: string | null;
  created_date: string;
  updated_date: string;
  // jsonb on the row; optional so existing object literals stay valid.
  comments?: TaskComment[] | null;
}

// ----- Form input shapes (subsets used by create/edit forms) ---------------

export type FeedbackFormInput = Omit<
  FacilityFeedback,
  | 'feedback_id'
  | 'resolution'
  | 'resolution_date'
  | 'resolved_by'
  | 'resolved_count'
  | 'recurrence_count'
> & {
  resolution?: string | null;
};

// ----- Supply Management ----------------------------------------------------
// Live schema, May 2026:
//   supplies.item_name (NOT supply_name) — see migration 20260504_supply_requests.sql
//   supplies.unit (NOT unit_of_measure)  — values: CASE, EACH, BOX, …
//   preferred_vendors.lead_time_days     — TEXT in DB ("2-5", "5", "next day")
//   preferred_vendors.same_day_capable   — TEXT yes/no rather than boolean

export type SupplyCategoryCode = 'CHM' | 'EQP' | 'PPE' | 'USE';

// Display labels for the four supply category codes seen in supplies.supply_category.
// Codes are TEXT in the DB; this map gives human-friendly strings for the UI.
export const SUPPLY_CATEGORY_LABELS: Record<SupplyCategoryCode, string> = {
  CHM: 'Chemicals',
  EQP: 'Equipment',
  PPE: 'PPE',
  USE: 'Disposables / Consumables',
};

export interface Supply {
  supply_id: string; // SUP-XXXX
  item_name: string | null;
  sku: string | null;
  supply_category: SupplyCategoryCode | string | null;
  sub_category: string | null;
  preferred_vendor: string | null; // human-readable vendor name (legacy column)
  vendor_id: string | null; // VND-XXXX → preferred_vendors.vendor_id
  unit: string | null; // CASE / EACH / BOX
  unit_cost: number | null;
  reimbursable: string | null; // "Yes" / "No" — TEXT in DB
  is_capital_equipment: string | null;
}

export interface PreferredVendor {
  vendor_id: string; // VND-XXXX
  vendor_name: string | null;
  primary_category: string | null;
  lead_time_days: string | null; // free-text in DB
  same_day_capable: string | null; // free-text in DB
  status: string | null;
  unit_price_avg: number | null;
  case_price_avg: number | null;
  delivery_fee: number | null;
}

export type SupplyUrgency = 'Emergency' | 'Soon' | 'Planned';

// Lifecycle milestones for a supply request. DB CHECK constraint enforces this
// exact set. Workflow substages (approval pending, vendor selected, delivery
// confirmed) live in dedicated columns (approved_at, vendor_selected, etc.) —
// not in status. The UI groups these into a shorter "Open / Approved /
// Fulfilling / Done" rollup for the request list.
//
// Reconciled to 8-state milestone lifecycle 2026-05-12. See
// PROJECTS/tcs-erp-database/artifacts/code-and-db-audit-2026-05-12.md §1.
export type SupplyRequestStatus =
  | 'Submitted'
  | 'Approved'
  | 'Rejected'
  | 'Ordered'
  | 'Shipped'
  | 'Delivered'
  | 'Fulfilled'
  | 'Cancelled';

export interface SupplyRequest {
  request_id: string; // REQ-XXXXXXXX
  request_batch_id: string | null; // groups N line items from one submission; NULL on rows from before the 2026-05 multi-item refactor
  facility_id: string | null;
  account_id: string | null;
  supply_id: string | null;
  item_description: string;
  quantity: number;
  unit_of_measure: string;
  urgency: SupplyUrgency;
  date_needed: string | null;
  requested_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  ship_to_address: string | null;
  reimbursable: boolean;
  notes: string | null;
  status: SupplyRequestStatus | string;
  estimated_cost: number | null;
  vendor_selected: string | null;
  created_at: string;
  updated_at: string;
}

// Shape of one line item inside a parsed supply batch. Fields are nullable
// because voice input is messy: the parser pre-fills what it can ("a case of
// virex" → quantity 1, unit CASE) and leaves the rest blank for the AM to
// resolve in the form.
export interface ParsedSupplyItem {
  item: string | null;
  quantity: number | null;
  unit: string | null; // CASE | EACH | BOX | BOTTLE | ROLL (uppercase)
  supply_id: string | null; // best-match SUP-XXXX, null when uncertain
}

// Envelope returned by the AI parser for a multi-item supply request.
// One facility, optional date_needed, default urgency, and N line items.
// See voice-capture.tsx parseTranscript() for the model contract.
export interface ParsedSupplyBatch {
  header: {
    facility: string | null; // partial-match string, resolved against active_facilities client-side
    facility_id: string | null;
    urgency: SupplyUrgency | null;
    date_needed: string | null; // ISO YYYY-MM-DD, or null
    notes: string | null;
  };
  items: ParsedSupplyItem[];
}

/**
 * @deprecated Use {@link ParsedSupplyBatch}. Kept exported for one release so
 * older callers compile while the multi-item refactor rolls out. Search for
 * remaining usages before removing.
 */
export interface ParsedSupplyRequest {
  item: string | null;
  quantity: number | null;
  unit: string | null;
  facility: string | null;
  facility_id: string | null;
  supply_id: string | null;
  urgency: SupplyUrgency | null;
  notes: string | null;
}

// ----- Account Management — Performance Snapshots ---------------------------
// Live schema, May 2026:
//   public.facility_performance_snapshots — see migration
//   20260504_facility_performance_snapshots.sql for DDL.
// Point-in-time metrics captured monthly (or on-demand) for trend analysis.

export interface FacilityPerformanceSnapshot {
  snapshot_id: string; // snap_<uuid>
  facility_id: string; // FK → facilities
  snapshot_date: string; // ISO date (DATE in DB)

  // Performance metrics at snapshot time
  open_feedback_count: number;
  resolved_this_month: number;
  avg_resolution_days: number | null;
  total_issues_ytd: number;

  // Status tracking at snapshot time
  rag_status: RagStatus | null;
  dominant_sentiment: FeedbackSentiment | null;

  // Ownership at snapshot time
  primary_am_id: string | null; // FK → employees

  // Metadata
  created_at: string; // ISO timestamptz
  created_by: string; // user_profiles.id or 'system'
}

// ----- Account Management — RAG history ------------------------------------
// Backed by public.v_facility_rag_history (security_invoker view).
// UNION of audit_log live UPDATEs of account_status_rag and the historical
// rag_status_backfill table (KPI-document import, 2026-05-21).
// `source` distinguishes the two: 'live' vs. e.g. 'KPI_Document_2026-05'.

export interface FacilityRagHistoryEntry {
  facility_id: string;
  rag_status_old: RagStatus | null;
  rag_status_new: RagStatus | null;
  rag_category: RagCategory | null;
  rag_reason: string | null;
  set_by_employee_id: string | null;
  set_by_name: string | null;
  set_at: string; // ISO timestamptz
  source: string; // 'live' | 'KPI_Document_2026-05' | future imports
}

// ----- HR & Hiring Pipeline -------------------------------------------------
// Live schema, May 2026 (reconciled 2026-05-12):
//   public.hiring_requests — primary key `request_id` TEXT, trigger-assigned
//   as HRQ-YYYY-NNNN (trg_hiring_requests_assign_id). Frontend MUST NOT supply
//   request_id on insert.
//
// HISTORY: Pre-2026-05-12 types described a fictional spec (id, role_title,
// description, transcript, lowercase status enum). The DB never matched. We
// reconciled the frontend to the actual DB shape rather than migrating the
// DB, because (a) 16 live rows already exist in DB shape, (b) the README
// canonical spec lives DB-side, (c) reverse migration would break running
// data. See COWORK-2026-05-12-EVENING-HR-HIRING-RECONCILE-REPORT.md.

// Urgency_label CHECK constraint enforces this set on the DB.
// (The DB also has a free-text `urgency` column shaped like "High (5)" — the
// frontend uses urgency_label for UI badges and treats `urgency` as raw text.)
export type HiringUrgency = 'High' | 'Medium' | 'Low';

// Status CHECK constraint enforces this set on the DB (7-stage Kanban,
// migration hr_hiring_pipeline_overhaul_2026_05_28):
//   CHECK (status IS NULL OR status = ANY (ARRAY[
//     'Open','Interview','Onboarding','Filled','On_Hold','Disregarded','No_Status']))
//
// The 2026-05-28 overhaul replaced the legacy 4-value set
// (Open | Covered | Not Covered | Withdrawn) with Antonela's Notion workflow.
// Legacy data was normalized in the same migration: Withdrawn → Disregarded,
// Covered → Filled. 'No_Status' is the triage bucket for rows that landed
// without a stage. See hr-hiring-pipeline-overhaul-2026-05-28-report.md.
export type HiringRequestStatus =
  | 'Open'
  | 'Interview'
  | 'Onboarding'
  | 'Filled'
  | 'On_Hold'
  | 'Disregarded'
  | 'No_Status';

// Canonical left-to-right order for the 7-column Kanban.
export const HIRING_STAGES: HiringRequestStatus[] = [
  'Open',
  'Interview',
  'Onboarding',
  'Filled',
  'On_Hold',
  'Disregarded',
  'No_Status',
];

// Column header labels per §4.1. 'Filled · Hired' reads as a terminal state.
export const HIRING_STAGE_LABELS: Record<HiringRequestStatus, string> = {
  Open: 'Open',
  Interview: 'Interview',
  Onboarding: 'Onboarding',
  Filled: 'Filled · Hired',
  On_Hold: 'On Hold',
  Disregarded: 'Disregarded',
  No_Status: 'No Status',
};

// Terminal / housekeeping stages get a lighter header + smaller count badge.
export const HIRING_TERMINAL_STAGES: HiringRequestStatus[] = [
  'Filled',
  'Disregarded',
  'No_Status',
];

// "Open-ish" stages — what counts as an active, unfilled requisition. Drives
// the Open Hiring Requests tile + analytics open-req rollups.
export const HIRING_OPEN_STAGES: HiringRequestStatus[] = [
  'Open',
  'Interview',
  'On_Hold',
];

// posting_source — where the job is ADVERTISED. Distinct from source_channel
// (how the request reached HR). Free-text in the DB; the form offers this set.
export const HIRING_POSTING_SOURCES = [
  'Indeed',
  'Internal',
  'Referred',
  'ZipRecruiter',
  'Other',
] as const;
export type HiringPostingSource = (typeof HIRING_POSTING_SOURCES)[number];

// source_channel is free-text on the DB (no CHECK). Current data uses only
// 'New Staffing Requests sheet' (the spreadsheet importer). README mentions
// Voice_AI / App_Form / Google_Chat / Email / Text / Manual as aspirational
// channels — we leave the type permissive.
export type HiringRequestSourceChannel = string;

export interface HiringRequest {
  request_id: string; // HRQ-YYYY-NNNN, trigger-assigned
  position_id: string | null;
  facility_id: string | null;
  account_id: string | null; // DB allows NULL — sheet imports may lack it
  requested_by: string | null; // text FK; NULL allowed
  title: string; // the role title (was role_title)
  role_type: string | null;
  shift: string | null;
  // Where the job is posted (Indeed / Internal / Referred / ZipRecruiter /
  // Other). Added 2026-05-28. Distinct from source_channel.
  posting_source: string | null;
  employment_type: string | null;
  rate_offered: number | null;
  urgency: string | null; // free-text composite "Label (Score)"
  urgency_label: HiringUrgency | null; // CHECK-constrained: High / Medium / Low
  urgency_score: number | null; // smallint, 1–5 expected
  target_start_date: string | null; // YYYY-MM-DD
  // A hiring request is ONE SEAT (item 621). A request for N people is stored
  // as N requests; the database does the split and holds every new row at 1.
  // Historical Disregarded requests may still read more than 1.
  headcount: number;
  // The request this seat was split from. NULL on the first seat and on every
  // single-seat request. The "2 of 3" on a card is counted from this, live.
  split_from_request_id: string | null;
  status: HiringRequestStatus | null;
  approved_by: string | null;
  approved_date: string | null;
  // TCSD-0162 — ownership is a POSITION with an optional named assignee, the
  // shape separation_checklist_items already uses. Replaces `hr_owner`, which
  // stored a person's name and went stale the moment that seat changed. Render
  // with ownerLabel() from shared/position-owner — ⛔ never the assignee alone.
  owner_position_key: string | null;
  assigned_to_eid: string | null;
  source_channel: HiringRequestSourceChannel | null;
  original_message: string | null; // raw voice transcript or email body (was transcript)
  notes: string | null; // free-text context (closest analog to old `description`)
  created_date: string | null;
  closed_date: string | null;
  closed_reason: string | null;
  created_at: string; // timestamptz, defaults to now()
  updated_at: string; // timestamptz, defaults to now()
}

// Shape of the parsed payload returned by the voice-capture AI parser. All
// fields optional — voice input is messy; the UI fills gaps with defaults
// and surfaces them as editable in the request form.
export interface ParsedHiringRequest {
  title: string | null; // was role_title
  headcount: number | null;
  urgency: HiringUrgency | null; // High / Medium / Low
  account: string | null; // free-text account name as the user said it
  account_id: string | null; // best-match ACC-XXXX from the catalog
  facility: string | null;
  facility_id: string | null;
  notes: string | null; // was description — context the parser extracted
}

// ----- New Account SOP ------------------------------------------------------
// Live schema, May 2026:
//   public.new_account_sop — 54 columns, 74 rows imported from Trello.
//   See migration 20260505_extend_new_account_sop_schema.sql for DDL.
//
// Stage canonical order (7 stages — README spec; current data has 5 of 7):
//   Created → Contractor_Info_Collected → Documents_Collected →
//   Ops_Review_Approval → Confirmation_Call_Complete → Executive_Review →
//   Billing_Active
//
// Status is a separate dimension (Active / Stalled). Stalled is shown as
// a UI flag (red border on the card), not as a Kanban column.

// ---------------------------------------------------------------------------
// NAS Phase 2 (2026-05-28): schema migration replaced the prior 7-stage
// enum with 5 active + Completed/Cancelled/Stalled. The new CHECK constraint
// on public.new_account_sop.stage enforces these exact values. Display labels
// match what BDDs/AMs already use in conversation.
// ---------------------------------------------------------------------------

export type NewAccountSopStage =
  | 'Info_and_Sub_Checklist'
  | 'Centralized_Confirmation_Call'
  | 'Executive_Review'
  | 'Final_Pre_Invoicing_Check'
  | 'Completed'
  | 'Cancelled'
  | 'Stalled';

// Active board columns (left-to-right in Kanban). Completed/Cancelled/Stalled
// are filterable but not first-class columns.
export type NewAccountSopActiveStage =
  | 'Info_and_Sub_Checklist'
  | 'Centralized_Confirmation_Call'
  | 'Executive_Review'
  | 'Final_Pre_Invoicing_Check'
  | 'Completed';

export type NewAccountSopStatus = 'Active' | 'Stalled' | 'Completed' | 'Cancelled';

export type NewAccountSopPriority = 'High' | 'Medium' | 'Low' | 'Normal';

export type NewAccountSopFacilityType =
  | 'Dialysis'
  | 'Surgery_Center'
  | 'Hospital'
  | 'MOB'
  | 'Other';

export interface NewAccountSop {
  // Identity
  sop_id: string; // SOP-XXXX
  account_id: string | null;
  account_name: string | null;
  facility_id: string | null;
  facility_name: string | null;
  // Market & lifecycle
  market_region: string | null;
  market_color: string | null;
  stage: NewAccountSopStage | string | null;
  status: NewAccountSopStatus | string | null;
  priority: NewAccountSopPriority | string | null;
  /**
   * AFB-0064 (2026-08-06). Manual archive stamp — the ONLY thing that removes a
   * card from the New Accounts board. Completion and age no longer do, because
   * the board is where Jose audits what is still missing on a started account.
   * Written by the superuser-only `nas_archive_card` RPC.
   */
  archived_at: string | null;
  archived_by_eid: string | null;
  days_in_stage: number | null;
  // Dates
  created_by_eid: string | null;
  created_date: string | null; // ISO date
  start_date: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Subcontractor
  contractor_name: string | null;
  contractor_phone: string | null;
  contractor_email: string | null;
  // Pricing & service
  // Renamed from monthly_payment → sub_payment (2026-05-29 trello-cutover #6):
  // this is what TCS pays the SUBCONTRACTOR, not what the client pays us
  // (that's final_pricing → "Client Monthly Price"). The two now live in
  // different card sections so they're never confused.
  sub_payment: number | null;
  payment_frequency: string | null;
  no_miss_incentive: number | null;
  final_pricing: number | null;
  // Management fee captured at intake (fraction; 0.0300 = 3%). NULL = not
  // captured; 0 = explicitly no fee. Carried to facilities.management_fee_pct
  // on materialization (mgmt-fee/kicker split, 2026-07-07).
  management_fee_pct: number | null;
  // AFB-0041 tri-state, captured at intake and — until AFB-0081 (2026-08-18) —
  // absent from this type, so nothing in the app could read or write it: the
  // column existed on the row, the answer was collected, and no screen could
  // show it. true = TCS supplies usables, false = the client provides their own,
  // NULL = nobody has answered. NULL must never render as "No".
  tcs_provides_usables: boolean | null;
  // Multiple service lines captured at intake (2026-07-14). jsonb array; each
  // element is fanned out into a service_assignments row by the materialize
  // trigger. line[0] is mirrored into final_pricing / sub_payment / the single
  // service_provider_id columns above for backward-compatible views.
  service_lines?: Record<string, unknown>[] | null;
  payment_terms: string | null;
  service_days_per_week: number | null;
  // Billing contact
  billing_contact_name: string | null;
  billing_contact_email: string | null;
  billing_contact_phone: string | null;
  billing_contact_address: string | null;
  // Client physical address
  client_address_street: string | null;
  client_address_city: string | null;
  client_address_state: string | null;
  client_address_zip: string | null;
  /**
   * The building's single address line, read through v_nas_card. The four
   * client_address_* boxes above are the INTAKE and stop being the answer the
   * moment the building exists — four boxes cannot round-trip one line, which
   * is how the card came to hold a second address that had to be reconciled.
   */
  facility_address?: string | null;
  /**
   * Operational fields the INTAKE alone still answers, because the building has
   * no value of its own yet. Empty on a card whose building has taken over
   * every one of them. Named on the row rather than hidden inside a COALESCE so
   * the screen can say which answer the reader is actually looking at.
   */
  intake_only_fields?: string[] | null;
  /**
   * False once the card is ARCHIVED or cancelled: it then changes nothing, on
   * either record. Completing the checklist does NOT close a card — admin
   * routinely finishes onboarding after go-live, and Finance archives it
   * afterwards. `archived_at` / `archived_by_eid` say since when and by whom.
   */
  card_is_open?: boolean | null;
  // Document links (Drive URLs)
  insurance_doc_link: string | null;
  employment_agreement_link: string | null;
  building_service_agreement_link: string | null;
  // Signed client proposal PDF (Drive URL). Business-confidential, not Tier-1
  // PII — lives on the row, not in partner_documents (AFB-0011).
  signed_proposal_url?: string | null;
  // Stage progression metadata
  ops_reviewer_eid: string | null;
  ops_approved_date: string | null;
  confirmation_call_date: string | null;
  confirmation_call_by: string | null;
  exec_review_date: string | null;
  exec_reviewer_eid: string | null;
  billing_active_date: string | null;
  billing_handoff_eid: string | null;
  // Linked records
  opportunity_id: string | null;
  onboarding_pipeline_id: string | null;
  // ⛔ The nine Trello columns are GONE from the table (2026-08-26, TCSD-0125):
  // card_title, trello_card_id, trello_list_name, trello_members,
  // trello_last_activity, trello_attachment_links, original_trello_url,
  // imported_date, and the dead importer nas_upsert_from_json that wrote them.
  // A card is named by its building, via resolveCardName() in
  // modules/new-account-sop/card-name.ts. What the columns held was preserved
  // first: 15 card titles are now searchable aliases on the building, and the
  // provenance of the 6 imported cards is in `notes` below.
  // ⛔ market_color is NOT Trello residue and is still live — the coloured
  // market dot on the card, list, drawer and create modal all read it.
  // Freeform
  notes: string | null;
  // ⛔ `account_name` is FREE TEXT captured at intake, not the customer's name.
  // The customer is `account_id` → `accounts.account_name`; the text drifts the
  // moment a building is reparented (10 of 38 rows disagreed on 2026-08-26).
  // use-sop-data.ts resolves it through the id before anything renders, so the
  // value on a row from that hook IS the account's name. Anything reading the
  // base table directly must do the same. TCSD-0118.
  // NAS Phase 2 schema additions (2026-05-28) — present on the row, not yet
  // edited by every component.
  facility_type?: NewAccountSopFacilityType | string | null;
  bdd_eid?: string | null;
  am_eid?: string | null;
  is_new_subcontractor?: boolean | null;
  service_provider_id?: string | null;
  bdd_completed_date?: string | null;
  am_sub_complete_date?: string | null;
  antonela_billing_approved?: boolean | null;
  antonela_billing_approved_date?: string | null;
  amy_qbo_sent_date?: string | null;
  jose_final_check_date?: string | null;
  checklist?: Record<string, unknown> | null; // jsonb — 7-stage structure from nas_derive_checklist
  attachments?: unknown[] | null;             // jsonb array
  cancelled_date?: string | null;
  cancelled_reason?: string | null;
  // Trello-cutover additions (DB applied 2026-05-29). Draft autosave so a
  // partial new-account form survives reload (#4); staged square footage
  // promoted to facility_details_* at activation (#9); margin estimator that
  // derives sub_payment from final_pricing (#6).
  is_draft?: boolean | null;
  draft_last_saved_at?: string | null;
  total_sqft?: number | null;
  sterile_sqft?: number | null;
  non_sterile_sqft?: number | null;
  target_margin_pct?: number | null;
  // AFB-0013 (DB applied 2026-06-01): extra billing CC recipients (comma-sep)
  // and OR/PR suite counts captured at create time.
  billing_cc_emails?: string | null;
  or_count?: number | null;
  pr_count?: number | null;
  // NEW ACCOUNTS lifecycle spine (Option D PR-4a, 2026-06-13). Set by the
  // confirm_account_started RPC — NULL = not yet started (still on the board).
  started_confirmed_at?: string | null;
  started_confirmed_by?: string | null;
  // AM go-live checklist (Option D PR-6). Map of item_key -> {done, by, at}.
  startup_checklist?: Record<string, StartupChecklistItemState> | null;
  // building_lifecycle() answer, read from v_new_account_lifecycle by the board
  // loader (2026-09-25). Not a table column; never computed in the app.
  lifecycle?: string | null;
}

export interface StartupChecklistItemState {
  done?: boolean;
  by?: string | null;
  at?: string | null;
}

// Active Kanban columns — left-to-right. Completed shows on the right as the
// terminal active column; Cancelled/Stalled are filtered into / out of the
// board by the status filter, not given their own column.
export const NEW_ACCOUNT_SOP_STAGES: NewAccountSopActiveStage[] = [
  'Info_and_Sub_Checklist',
  'Centralized_Confirmation_Call',
  'Executive_Review',
  'Final_Pre_Invoicing_Check',
  'Completed',
];

// Display labels — DB stores enum codes with underscores. Completed/Cancelled/
// Stalled labels included so detail surfaces and filter chips render
// human-friendly text.
export const NEW_ACCOUNT_SOP_STAGE_LABELS: Record<NewAccountSopStage, string> = {
  Info_and_Sub_Checklist: 'Info & Sub Checklist',
  Centralized_Confirmation_Call: 'Centralized Confirmation Call',
  Executive_Review: 'Executive Review',
  Final_Pre_Invoicing_Check: 'Final Pre-Invoicing Check',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
  Stalled: 'Stalled',
};

// ⛔ THE MARKET LIST AND THE MARKET COLOURS ARE NOT WRITTEN DOWN HERE ANY MORE.
// They are rows in `public.markets` and they reach the app through
// `shared/markets.ts` — `useMarkets()` for the list, `marketOptions()` for a
// picker's options, `marketColorPair()` for the dot.
//
// This file used to hold the eleven metros verbatim "MUST match the DB CHECK",
// alongside eleven hex literals, and that instruction was the problem: two
// copies of one fact, kept in step by a comment. Adding a market meant a
// migration AND a deploy AND remembering the design tokens held a third,
// state-shaped copy. It is now a row.
//
// The two constants are deliberately GONE rather than reassigned at runtime: an
// exported `const` that is really a mutable cache reads as a compile-time truth
// and is a trap for the next reader.

// Legacy Trello label → canonical metro, for resuming old drafts. Mirrors the
// unambiguous cases of the DB nas_canonical_market() helper. Ambiguous labels
// (Texas, Colorado, Missouri, Nebraska, Florida) are intentionally ABSENT —
// they can't resolve to a single metro, so a resumed draft carrying one falls
// back to '' and the user re-picks.
export const NAS_LEGACY_MARKET_MAP: Record<string, string> = {
  'St. Louis': 'St. Louis Metro',
  'Kansas City': 'Kansas City Metro',
  'Dallas-Fort Worth': 'Dallas-Fort Worth Metro',
  'Denver': 'Denver Metro',
  'Houston': 'Houston Metro',
  'Austin': 'Austin Metro',
  'San Antonio': 'San Antonio Metro',
  'Omaha': 'Omaha Metro',
  'Orlando': 'Orlando Metro',
  'Reno': 'Reno Metro',
  'Colorado Springs': 'Colorado Springs Metro',
};

export const NAS_FACILITY_TYPE_OPTIONS: NewAccountSopFacilityType[] = [
  'Dialysis',
  'Surgery_Center',
  'Hospital',
  'MOB',
  'Other',
];

export const NAS_FACILITY_TYPE_LABELS: Record<NewAccountSopFacilityType, string> = {
  Dialysis: 'Dialysis',
  Surgery_Center: 'Surgery Center',
  Hospital: 'Hospital',
  MOB: 'MOB',
  Other: 'Other',
};

// ===========================================================================
// AM Phase 2 — Sessions + Incentive Payouts (DB applied 2026-05-11)
// Migration: supabase/migrations/20260511_sessions_and_incentive_payouts.sql
// Spec: app-readmes/account-management-README.md § "New Tables"
// ===========================================================================

// Mirrors public.sessions CHECK (session_type IN (...)).
export type SessionType = 'Meeting' | 'Audit' | 'Site Visit' | 'Other';

// "Start a Session" capture. Facility-anchored work events; auto-logs to the
// activity feed (facility_feedback) on session end when auto_logged_to_activity
// is true. Virtual / phone sessions may omit facility_id.
export interface Session {
  session_id: string; // SES-XXXXXXXX
  session_type: SessionType;
  facility_id: string | null;
  account_id: string | null; // Derived from facility on insert via trigger
  am_eid: string; // EID-XXXX of the AM running the session
  started_at: string; // ISO timestamptz
  ended_at: string | null;
  participants: string[] | null; // Free-text names + emails
  notes: string | null;
  attached_photos: string[] | null; // URLs / Drive links
  outcomes: string | null;
  auto_logged_to_activity: boolean;
  created_at: string;
  updated_at: string;
}

// Form input shape for the "Start a Session" capture. ID + timestamps are
// server-stamped; the trigger derives account_id from facility_id.
export type SessionFormInput = Omit<
  Session,
  'session_id' | 'account_id' | 'created_at' | 'updated_at'
> & {
  // started_at can be omitted to let the DB default to now().
  started_at?: string;
};

// Mirrors public.incentive_payouts CHECK constraints.
export type IncentivePayoutType =
  | 'no_misses_monthly'
  | 'compliment_quarterly';

export type IncentivePayoutStatus =
  | 'pending_review'
  // Two-step validation (2026-08-13, CW-N). The account manager who owns the
  // facility validates their own row; the National AM then approves it. The
  // National AM may also skip straight to 'verified' so a silent AM cannot
  // block payroll.
  | 'am_validated'
  | 'verified'
  | 'sent_to_finance'
  | 'paid'
  // Non-paying terminal for a No-Miss row disqualified by a complaint
  // (counts_against_nmi). Added 2026-06-05 (incentive program). An AM can
  // override it back to verified with a justification.
  | 'disqualified';

// 'crew' = one lump quarterly compliment covering the crew + sub leader
// (amount = rate × (crew_size + 1)). Added 2026-06-05.
export type IncentivePayoutRecipientType =
  | 'worker'
  | 'provider_company'
  | 'crew';

// Subcontractor incentive payout. Lifecycle (two-step as of 2026-08-13):
//   pending_review (accrued by the engine)
//     → am_validated (the facility's own AM validates, gated by trigger)
//     → verified (national_am approves; may also jump straight from
//                 pending_review so a silent AM cannot block payroll)
//     → sent_to_finance (finance routes to QBO)
//     → paid (finance closes after QBO bill paid)
// 'disqualified' is reachable from pending_review or am_validated and is
// reversible back out ("reinstate"). Every transition is enforced by
// incentive_payouts_enforce_verifier_scope — if a button throws here, the
// role gating in the UI is wrong, not the database.
export interface IncentivePayout {
  payout_id: string; // INP-XXXXXXXX
  payout_type: IncentivePayoutType;
  period: string; // '2026-04' (monthly) or '2026-Q2' (quarterly)
  assignment_id: string | null;
  provider_id: string | null;
  recipient_type: IncentivePayoutRecipientType;
  amount: number;
  status: IncentivePayoutStatus;
  // Step 1 — the owning AM's stamp (CW-N, 2026-08-13).
  am_validated_by_eid: string | null;
  am_validated_at: string | null; // ISO timestamptz
  // Step 2 — the approver's stamp. Unchanged meaning.
  verified_by_eid: string | null;
  verified_at: string | null; // ISO timestamptz
  routed_to_finance_at: string | null;
  paid_at: string | null;
  qbo_bill_id: string | null;
  // Incentive program 2026-06-05 additions:
  disqualifying_feedback_id?: string | null; // FK → facility_feedback; why a No-Miss row did not pay
  override_justification?: string | null;     // required to flip the engine's decision
  overridden_by_eid?: string | null;          // who entered the override
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Canonical lifecycle order — drives review-queue rendering. 'disqualified'
// sits at the end as the non-paying terminal state.
export const INCENTIVE_PAYOUT_STATUSES: IncentivePayoutStatus[] = [
  'pending_review',
  'am_validated',
  'verified',
  'sent_to_finance',
  'paid',
  'disqualified',
];

export const INCENTIVE_PAYOUT_STATUS_LABELS: Record<
  IncentivePayoutStatus,
  string
> = {
  pending_review: 'Pending review',
  am_validated: 'Validated — awaiting approval',
  verified: 'Verified',
  sent_to_finance: 'Sent to finance',
  paid: 'Paid',
  disqualified: 'Disqualified',
};

export const INCENTIVE_PAYOUT_TYPE_LABELS: Record<
  IncentivePayoutType,
  string
> = {
  no_misses_monthly: 'Scope Performance Payment (monthly)',
  compliment_quarterly: 'Compliment payment (quarterly)',
};


// ============================================================================
// Sub Replacement (Phase 1) — 2026-05-12
//
// Tables: sub_replacement_requests (PK: request_id, SRR-NNNN),
//         sub_replacement_candidates (PK: candidate_id, SRC-NNNN, empty in Phase 1),
//         sub_replacement_handoffs (PK: handoff_id, SRH-NNNN, empty in Phase 1).
//
// Phase 1 surface: AM creates the request (status='Pending'), has_full_am_authority
// roles (superuser / executive / national_am) approve or reject. Candidate
// matching + handoff flow are Phase 2/3.
//
// Status enum admits the Phase 2/3 values (Matching, Awaiting_Onboarding,
// Cutover_Pending, Completed) so future writes don't need a schema migration.
// ============================================================================

export type SubReplacementUrgency = 'Emergency' | 'Soon' | 'Planned';

export type SubReplacementStatus =
  | 'Pending'
  | 'Approved'
  | 'Rejected'
  | 'Cancelled'
  | 'Matching'
  | 'Awaiting_Onboarding'
  | 'Cutover_Pending'
  | 'Completed';

// 2026-06-09 (AFB-0027): change-date semantics. 'planned' = future swap;
// 'historical' = backdated swap that already happened in the field.
export type SubReplacementChangeDateType = 'planned' | 'historical';

export interface SubReplacementRequest {
  request_id: string; // SRR-NNNN
  facility_id: string;
  account_id: string;
  outgoing_assignment_id: string | null;
  /** Optional intent — incoming sub when known at request time. */
  incoming_provider_id: string | null;
  requested_by_eid: string;
  reason: string | null;
  /** Why the sub is changing — a sub_change code from facility_lifecycle_reasons
   *  (#667). What the loss screens read; `reason` is the words. */
  reason_code: string | null;
  urgency: SubReplacementUrgency;
  hold_pay: boolean;
  /** New sub's pay. NULL = pay unchanged from the outgoing assignment. */
  incoming_pay_rate: number | null;
  nmi_enabled: boolean | null;
  nmi_amount: number | null;
  compliment_enabled: boolean | null;
  compliment_amount: number | null;
  planned_change_date: string | null; // ISO date
  change_date_type: SubReplacementChangeDateType | null;
  notes: string | null;
  status: SubReplacementStatus;
  approved_by_eid: string | null;
  approved_date: string | null;
  rejection_reason: string | null;
  created_by_eid: string | null;
  created_date: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Joined shape returned by the list/detail queries. Embeds the labels the
// UI needs without re-fetching per row.
export interface SubReplacementRequestRow extends SubReplacementRequest {
  facilities: { facility_name: string | null; market_region: string | null } | null;
  accounts: { account_name: string | null } | null;
  requester: { employee_name_first: string | null; employee_name_last: string | null } | null;
  approver: { employee_name_first: string | null; employee_name_last: string | null } | null;
  outgoing_assignment: {
    assignment_id: string;
    role_title: string | null;
    provider_id: string | null;
    /** Resolved via service_providers.partner_name on provider_id. */
    provider_name: string | null;
    /**
     * Item 896: the outgoing line's sub pay basis. 'hourly' means the
     * request's incoming_pay_rate is dollars per hour; anything else, per month.
     */
    sub_rate_type?: string | null;
    sub_hourly_rate?: number | null;
  } | null;
  /**
   * Incoming (new) sub, resolved via service_providers.partner_name on
   * incoming_provider_id. Null when no incoming sub is captured yet.
   * (AFB-0051 — the detail page must render this.)
   */
  incoming_provider: { partner_name: string | null } | null;
}

export interface NewSubReplacementRequestInput {
  facility_id: string;
  account_id: string;
  outgoing_assignment_id: string | null;
  /**
   * Optional intent — when the AM already knows who the incoming sub will
   * be at request time, capture it here. Nullable; the actual handoff is
   * tracked in sub_replacement_handoffs. Added 2026-05-20.
   */
  incoming_provider_id: string | null;
  requested_by_eid: string;
  reason: string;
  /** Required: a sub_change code the form offers (#667). */
  reason_code: string;
  urgency: SubReplacementUrgency;
  hold_pay: boolean;
  /** AFB-0027: NULL = keep the current rate (must NOT coerce blank to 0). */
  incoming_pay_rate: number | null;
  nmi_enabled: boolean | null;
  nmi_amount: number | null;
  compliment_enabled: boolean | null;
  compliment_amount: number | null;
  planned_change_date: string | null;
  change_date_type: SubReplacementChangeDateType | null;
  notes: string | null;
}

export const SUB_REPLACEMENT_URGENCY_TIERS: SubReplacementUrgency[] = [
  'Emergency',
  'Soon',
  'Planned',
];

export const SUB_REPLACEMENT_URGENCY_LABEL: Record<SubReplacementUrgency, string> = {
  Emergency: 'Emergency',
  Soon: 'Soon',
  Planned: 'Planned',
};

// Tier sort key — lower number = higher priority — used by the approval queue.
export const SUB_REPLACEMENT_URGENCY_RANK: Record<SubReplacementUrgency, number> = {
  Emergency: 0,
  Soon: 1,
  Planned: 2,
};

// ============================================================================
// Project Work (Phase 1) — 2026-05-13.
// ============================================================================

// 2026-08-20: ProjectStatus and ProjectPriority retired with the columns they
// described. `status` was a second lifecycle beside `stage` that no code ever
// advanced — and as a default list filter it hid 340 of 354 Archive rows
// ($243,509.77 of billings). `priority` was never written by the entry screen.

// Billing pipeline stages (DB CHECK constraint).
// Draft → Ready_to_Invoice → Pending_PO → Invoiced → Completed
// 2026-05-29 (AFB-0009): Awaiting_AM_Input inserted after Draft — finance/exec
// can bounce a Ready_to_Invoice card back here for the AM to fill missing data.
// 2026-06-02: Pending_PO inserted before Invoiced — finance has all financial
// info and has sent the client the billing notice, but is waiting on the client
// to generate a PO number. Out of TCS's hands; follow-ups logged in the project
// activity log (project_notes).
export const PROJECT_STAGES = ['Draft', 'Awaiting_AM_Input', 'Ready_to_Invoice', 'Pending_PO', 'Invoiced', 'Completed'] as const;
export type ProjectStage = (typeof PROJECT_STAGES)[number];

// ⛔ "Draft" and "Awaiting AM Input" ARE THE SAME THING and now read the same —
// Ben, addendum 2D, 2026-09-03: "One label. Use Not sent."
//
// Measured that day: `Awaiting_AM_Input` is a REAL stored CHECK value, not a
// rendered alias — which the addendum warned would be "worse than a duplicate" —
// and it carries ZERO rows. Exactly one project has ever been bounced back.
//
// ⛔ THE STAGE IS NOT REMOVED. Finance's bounce-back (AFB-0009,
// `sendProjectBackToAm`) still writes it, still stamps who/when/why, and the
// reason still renders inline on the card. What went is the AM having to learn
// that two words mean one state. Removing the stage itself would delete a
// finance capability that 2D did not ask to remove.
export const PROJECT_STAGE_LABELS: Record<ProjectStage, string> = {
  Draft: 'Not sent',
  Awaiting_AM_Input: 'Not sent',
  Ready_to_Invoice: 'Ready to Invoice',
  Pending_PO: 'Pending PO',
  Invoiced: 'Invoiced',
  Completed: 'Completed',
};

export const PROJECT_STAGE_CLASSES: Record<ProjectStage, string> = {
  Draft: 'bg-tcs-surface text-tcs-text-secondary',
  // Distinct from Ready_to_Invoice's amber — orange reads as "action needed".
  Awaiting_AM_Input: 'bg-st-warn-bg-strong text-st-warn-fg',
  Ready_to_Invoice: 'bg-st-warn-bg-strong text-st-warn-fg',
  // Violet = "waiting on the client" (out of our hands), distinct from the
  // blue Invoiced state that follows it.
  Pending_PO: 'bg-violet-100 text-violet-700',
  Invoiced: 'bg-st-info-bg-strong text-st-info-fg',
  Completed: 'bg-st-ok-bg-strong text-st-ok-fg',
};

// 2026-08-20: PROJECT_STATUS_CLASSES and PROJECT_PRIORITY_CLASSES are gone with
// projects.status and projects.priority. `stage` is the only lifecycle a project
// has. See the drop migration for what those columns held and why they went.

export interface Project {
  project_id: string;
  account_id: string | null;
  facility_id: string | null;
  account_name: string | null;
  facility_name: string | null;
  // 2026-08-18 (Run A): renamed from service_type / category. Both are READ-ONLY
  // AUDIT columns now — the historic free text, kept so the original wording of
  // a row is never lost. The live vocabulary is project_services.service_code.
  service_type_raw: string | null;
  start_date: string | null;
  completion_date: string | null;
  notes: string | null;
  assigned_partner_id: string | null;
  bill_amount: number | null;
  pay_amount: number | null;
  margin_amount: number | null;
  due_date: string | null;
  created_by_eid: string | null;
  stage: string | null;
  category_raw: string | null;
  created_date: string | null;
  margin_status: string | null;
  margin_pct: number | null;
  updated_by_eid: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Billing lifecycle (2026-05-22).
  ready_to_invoice_at: string | null;
  // Pending PO — finance is waiting on the client's PO number (2026-06-02).
  pending_po_at: string | null;
  invoiced_at: string | null;
  invoiced_by_eid: string | null;
  // Send-back-to-AM audit (2026-05-29, AFB-0009).
  awaiting_am_input_at: string | null;
  awaiting_am_input_by_eid: string | null;
  awaiting_am_input_reason: string | null;
  awaiting_am_input_resolved_at: string | null;
  // Awaiting client schedule — work is known but no date yet / waiting on client (2026-06-03).
  awaiting_schedule: boolean;
  // AFB-0022 #3 — subcontractor special/one-off pay. special_pay is a MINT GATE,
  // not a classification: true means never auto-mint, a human with finance
  // capability must mint manually with override.
  special_pay: boolean;
  // 2026-08-18 (Run A): special_pay_note was dropped; its words live in
  // reason_note, which now serves every kind of "this is not ordinary billable
  // work" explanation. Required by a DB CHECK whenever event_class is not
  // 'Project'.
  reason_note: string | null;
  event_class: string;
  reason_code: string | null;
  // Embedded service lines (project_services). Present when the caller asked
  // PostgREST to embed them; a project created before the backfill can have none.
  project_services?: ProjectServiceLine[] | null;
  // Item #680: the import source (Trello backfill rows are skipped by the sub-pay
  // alert and chip) and the whole-project payment record, when the list embeds them.
  source?: string | null;
  partner_payments?: { payment_id: string }[] | null;
}

// One service performed under a project event. Replaces the single
// projects.service_type string: each line carries its own scope, rates, sub and
// dates, so a three-service project renders as three invoice lines and pays
// three different subs. 2026-08-18 (Run A Phase 1.2).
export interface ProjectServiceLine {
  service_line_id: string;
  project_id: string;
  /**
   * 'service' — work performed, paid TO a sub.
   * 'chargeback' — money recovered FROM a sub for something they caused.
   * A chargeback has no service_code, a negative sub_rate, and a required reason
   * in scope_text. Enforced by project_services_chargeback_is_complete.
   */
  line_role: string;
  /** NULL on a chargeback line — a chargeback is not a service. */
  service_code: string | null;
  scope_text: string | null;
  scope_qty: number | null;
  scope_unit: string | null;
  client_rate: number | null;
  sub_rate: number | null;
  line_margin_pct: number | null;
  assigned_partner_id: string | null;
  service_date_planned: string | null;
  service_date_actual: string | null;
  // Derived in the DB: actual date => Completed, else planned date => Scheduled,
  // else Estimate. Both date columns are kept, so changing the plan never
  // erases what was first scheduled.
  timing_state: string | null;
  line_order: number | null;
  notes: string | null;
  // Embedded from service_catalog via the service_code FK, so nothing in the
  // frontend has to keep its own copy of the display names.
  service_catalog?: { display_name: string | null; division_id: string | null } | null;
  // Item #680: the payment record on this line (a "Pay this line" record or a
  // linked AP-report row), when the list embeds it.
  partner_payments?: { payment_id: string }[] | null;
}

export interface ProjectRow extends Project {
  facilities: {
    facility_name: string | null;
    market_region: string | null;
    status: string | null;
    // AM-on-card support (AFB-0006). EID is source of truth; facilities.account_manager_name
    // exists in the DB but is occasionally stale — never render that column directly.
    // Resolved client-side via a flat-fetch in use-project-data.ts.
    account_manager_eid?: string | null;
    account_manager?: {
      employee_id: string;
      employee_name_first: string | null;
      employee_name_last: string | null;
    } | null;
  } | null;
  accounts: { account_name: string | null } | null;
  partner: { provider_id: string; provider_name: string | null } | null;
  creator: { employee_name_first: string | null; employee_name_last: string | null } | null;
}

// NewProjectInput was retired in Run B Phase 2 (2026-08-18). It described a
// project with exactly one service on it, which stopped being what a project is
// when Run A gave it a set of service lines. Its replacement,
// NewProjectWithLinesInput, lives next to the write that uses it in
// project-work/hooks/use-project-data.ts rather than in this shared file — it
// has one caller and no reason to be reachable from anywhere else.

export interface UpdateProjectInput {
  // service_type / category are gone: the service is edited on the project's
  // service line, not on the project row. See saveProjectServiceLine().
  stage?: ProjectStage | string | null;
  start_date?: string | null;
  due_date?: string | null;
  completion_date?: string | null;
  bill_amount?: number | null;
  pay_amount?: number | null;
  assigned_partner_id?: string | null;
  notes?: string | null;
  ready_to_invoice_at?: string | null;
  invoiced_at?: string | null;
  invoiced_by_eid?: string | null;
  // Send-back-to-AM audit (2026-05-29, AFB-0009).
  awaiting_am_input_at?: string | null;
  awaiting_am_input_by_eid?: string | null;
  awaiting_am_input_reason?: string | null;
  awaiting_am_input_resolved_at?: string | null;
  awaiting_schedule?: boolean | null;
  // AFB-0022 #3 — special/one-off sub pay flag; the words go in reason_note.
  special_pay?: boolean | null;
  reason_note?: string | null;
  event_class?: string | null;
  reason_code?: string | null;
  // 2026-07-20 — link/relink a project to a facility (+ derived account) from
  // the Project detail screen. facility_name/account_name are denormalized on
  // the projects row alongside the ids.
  facility_id?: string | null;
  facility_name?: string | null;
  account_id?: string | null;
  account_name?: string | null;
}


// ────────────────────────────────────────────────────────────────────────────
// AM Co-pilot (Phase 1, 2026-05-18)
// ────────────────────────────────────────────────────────────────────────────

// Batch 3 (item 944): every building action is propose_building_action. A
// draft saved under one of the retired building tool names (feedback, close
// feedback, communication log, field / access update, RAG, status change)
// still loads from history; the chat shows it as an earlier draft with the
// building's link, and it cannot be committed.
export type CopilotToolName =
  | "propose_task"
  | "propose_supply_order"
  | "propose_close_task"
  | "propose_hiring_request"
  | "propose_sub_replacement"
  | "propose_supply_par_update"
  | "propose_project_work"
  | "propose_add_subcontractor"
  | "propose_stock_movement"
  | "propose_board_action"
  | "propose_building_action"
  | "propose_project_action"
  | "propose_new_account_action"
  | "propose_task_action"
  | "propose_time_action"
  | "propose_my_work_action"
  | "propose_sub_action"
  | "propose_hiring_action"
  | "propose_onboarding_action"
  | "propose_supply_action"
  | "propose_machine_action"
  | "propose_separation_item"
  | "propose_mileage";

export type CopilotProposalStatus =
  | "Proposed"
  | "Committed"
  | "Edited_And_Committed"
  | "Discarded";

export interface CopilotProposalBase {
  action_id: string; // COP-NNNN, server-assigned
  tool_name: CopilotToolName;
  // The original AI proposal payload — never mutated client-side after
  // creation. Edits live in card-local state and get diffed against this on
  // commit to decide between Committed vs Edited_And_Committed.
  input: Record<string, unknown>;
  status: CopilotProposalStatus;
  // FBK-NNNN / FTK-NNNN once committed
  target_row_id: string | null;
  // A committed close: how many really closed of how many asked (read back
  // from the write, recorded in copilot_actions.committed_payload).
  result?: { done: number; of: number };
}

export interface CopilotMessage {
  role: "user" | "assistant";
  // Display text. For assistant messages this is the model's text reply
  // (NOT the proposal payloads — those render as cards beneath).
  content: string;
  // Present only on assistant messages; one entry per propose_* tool call.
  proposals?: CopilotProposalBase[];
}

export interface CopilotSession {
  session_id: string; // client-generated UUID, stable for the conversation
  messages: CopilotMessage[];
}

// Server response shape returned by the am-copilot Edge Function.
export interface CopilotInvokeResponse {
  message: {
    role: "assistant";
    // Full Anthropic content blocks (text + tool_use) — useful for replaying
    // a turn into the conversation on the next invocation.
    content: unknown[];
  };
  proposals: Array<{
    action_id: string;
    tool_name: CopilotToolName;
    input: Record<string, unknown>;
  }>;
  usage: { input_tokens: number; output_tokens: number };
  request_id: string;
  hit_iteration_cap?: boolean;
  // Canonical chat ID — added 2026-05-21 with the persistent-chat layer.
  // Always populated. The client persists it and sends it back on every
  // follow-up turn so the server appends to the same conversation row.
  conversation_id?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// AM Co-pilot persistent chat (added 2026-05-21)
// ────────────────────────────────────────────────────────────────────────────

export interface CopilotConversation {
  id: string;
  user_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
}

export interface CopilotMessageRow {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string | null;
  // JSONB columns — shape matches Anthropic SDK content blocks
  // (tool_use entries on tool_calls, tool_result entries on tool_results).
  tool_calls: unknown;
  tool_results: unknown;
  tokens_used: number | null;
  created_at: string;
}


// ----- Supply Management — Procurement (Phase 1, 2026-05-24) ---------------
// purchase_orders, purchase_order_lines, vendor_supply_pricing types.
// Live column names preserved from existing schema: po_date is TEXT in DB,
// requested_by_eid is FK to employees(employee_id); qty/unit_price on lines
// (not quantity/unit_cost). See migration
// supply_management_procurement_phase1_2026_05_24.

export type PurchaseOrderStatus =
  | 'Draft'
  | 'Approved'
  | 'Sent'
  | 'Acknowledged'
  | 'Shipped'
  | 'Delivered'
  | 'Invoiced'
  | 'Closed'
  | 'Cancelled';

export interface PurchaseOrder {
  po_id: string; // PO-XXXXX
  po_date: string | null; // TEXT in DB
  requested_by_eid: string | null;
  requested_by_name: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  facility_id: string | null;
  facility_name: string | null;
  account_id: string | null;
  ship_to_address: string | null;
  urgency: import('./types').SupplyUrgency | string | null;
  status: PurchaseOrderStatus | string | null;
  approval_required: string | null;
  approved_by_eid: string | null;
  approval_date: string | null;
  submitted_date: string | null;
  expected_delivery: string | null;
  actual_delivery: string | null;
  subtotal: number | null;
  tax: number | null;
  shipping: number | null;
  total: number | null;
  payment_method: string | null;
  reimbursable: string | null;
  qbo_synced: string | null;
  invoice_number: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type PurchaseOrderLineStatus =
  | 'Ordered'
  | 'Shipped'
  | 'Delivered'
  | 'Verified';

export interface PurchaseOrderLine {
  line_id: string; // POL-XXXXXX
  po_id: string;
  request_id: string | null; // back-link to source supply_request
  supply_id: string | null;
  item_name: string | null;
  sku: string | null;
  qty: number;
  unit: string | null;
  unit_price: number | null;
  extended_price: number | null;
  facility_id: string | null;
  account_id: string | null;
  reimbursable: string | null;
  category: string | null;
  notes: string | null;
  line_status: PurchaseOrderLineStatus | string | null;
  estimated_delivery: string | null;
  actual_delivery: string | null;
  estimated_sales_tax: number | null;
  ship_to_address: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorSupplyPricing {
  vsp_id: string;
  vendor_id: string | null;
  vendor_name: string | null;
  supply_id: string | null;
  item_name: string | null;
  vendor_sku: string | null;
  vendor_item_name: string | null;
  unit_price: number | null;
  case_price: number | null;
  uom: string | null;
  pack_size: string | null;
  lead_time_days: string | null;
  contract_price_yn: string | null;
  notes: string | null;
}
